import { useEffect, useId, useRef, useState } from 'react';

const TOOLTIP_MS = 2800;

function PrimaryButton({
  children,
  disabled = false,
  loading = false,
  type = 'button',
  onClick,
  showArrow = true,
  className = '',
  disabledTooltip,
  showDisabledTooltip = false,
  onShowDisabledTooltip,
}) {
  const tooltipId = useId();
  const [internalTip, setInternalTip] = useState(false);
  const tipTimerRef = useRef(null);
  const allowsDisabledClick = Boolean(disabledTooltip);
  const showTip =
    Boolean(disabledTooltip) && (showDisabledTooltip || internalTip);

  useEffect(() => {
    return () => {
      if (tipTimerRef.current) {
        clearTimeout(tipTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!disabled) {
      setInternalTip(false);
    }
  }, [disabled]);

  const revealDisabledTooltip = () => {
    if (!disabledTooltip) {
      return;
    }
    onShowDisabledTooltip?.();
    setInternalTip(true);
    if (tipTimerRef.current) {
      clearTimeout(tipTimerRef.current);
    }
    tipTimerRef.current = setTimeout(() => {
      setInternalTip(false);
    }, TOOLTIP_MS);
  };

  const handleClick = (event) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    if (disabled && allowsDisabledClick) {
      event.preventDefault();
      revealDisabledTooltip();
      return;
    }
    if (disabled) {
      return;
    }
    onClick?.(event);
  };

  const isBusy = Boolean(loading);
  const looksDisabled = disabled && !isBusy;

  return (
    <div className="onboarding-primary-btn-wrap">
      <button
        type={type}
        className={`onboarding-primary-btn ${looksDisabled ? 'is-disabled' : ''} ${isBusy ? 'is-loading' : ''} ${className}`.trim()}
        disabled={(disabled || isBusy) && !allowsDisabledClick ? true : undefined}
        aria-disabled={disabled || isBusy || undefined}
        aria-busy={isBusy || undefined}
        aria-describedby={showTip ? tooltipId : undefined}
        onClick={handleClick}
      >
        {isBusy ? (
          <span className="onboarding-primary-btn__spinner" aria-hidden />
        ) : null}
        <span>{children}</span>
        {showArrow && !isBusy ? (
          <span className="onboarding-primary-btn__arrow" aria-hidden>
            →
          </span>
        ) : null}
      </button>
      {showTip ? (
        <div
          id={tooltipId}
          role="tooltip"
          className="onboarding-primary-btn-tooltip"
        >
          {disabledTooltip}
        </div>
      ) : null}
    </div>
  );
}

export default PrimaryButton;
